/**
 * Run Now — trigger a schedule, then show that run as a checklist: submitted, running, completed
 * (success exit code 0 | failure exit code N | failure awol), with a button that opens its logs in a new tab.
 *
 * Imperative trigger: openRunNowDialog({ scheduleUid, scheduleName }) from anywhere. The Subject
 * emits to the always-mounted RunNowDialogHost.
 */
import React, { useEffect, useRef, useState } from "react";
import { Alert, Button, CircularProgress, Dialog, DialogActions, DialogContent, DialogTitle, List, ListItem, ListItemIcon, ListItemText, TextField } from "@mui/material";
import OpenInNewIcon from "@mui/icons-material/OpenInNew";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import ErrorIcon from "@mui/icons-material/Error";
import RadioButtonUncheckedIcon from "@mui/icons-material/RadioButtonUnchecked";
import { ROUTES } from "../../routes/paths";
import { notify } from "../notifications/NotificationProvider";
import { followRun, logRunFor, runJobNow, runStateLabel, runSteps, type RunState, type RunStep } from "../../services/scheduler-rpc-client";
import { showRunNowDialog, type RunNowDialogRequest } from "./run-now-signal";

export const RunNowDialogHost: React.FC = () => {
  const [req, setReq] = useState<RunNowDialogRequest | undefined>(undefined);

  useEffect(() => {
    const sub = showRunNowDialog.subscribe((next) => setReq(next));
    return () => sub.unsubscribe();
  }, []);

  // Keyed by schedule so a second Run Now starts from the reason form, not the last run's state.
  return req ? <RunNowDialog key={req.jobEventUid ?? req.scheduleUid} req={req} onClose={() => showRunNowDialog.next(undefined)} /> : null;
};

function stepIcon(status: RunStep["status"]) {
  switch (status) {
    case "done":
      return <CheckCircleIcon color="success" />;
    case "failed":
      return <ErrorIcon color="error" />;
    case "waiting":
      return <CircularProgress size={20} />;
    case "pending":
      return <RadioButtonUncheckedIcon color="disabled" />;
  }
}

const RunNowDialog: React.FC<{ req: RunNowDialogRequest; onClose: () => void }> = ({ req, onClose }) => {
  const [reason, setReason] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | undefined>(undefined);
  const [state, setState] = useState<RunState | undefined>(undefined);
  // The follow outlives the dialog: a run closed on before it ends still reports how it ended.
  const open = useRef(true);
  useEffect(() => () => void (open.current = false), []);

  const follow = (jobEventUid: string) =>
    void followRun(jobEventUid, (s) => open.current && setState(s)).then((last) => {
      if (open.current) return;
      const done = last.phase === "completed" || last.phase === "not-started";
      if (done) notify({ severity: last.phase === "completed" && last.outcome === "success" ? "success" : "error", title: req.scheduleName, message: runStateLabel(last) });
    });

  useEffect(() => {
    if (req.jobEventUid) follow(req.jobEventUid);
    // one follow per dialog: the host keys the dialog by jobevent
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const submit = async () => {
    setSubmitting(true);
    setError(undefined);
    try {
      const { jobEventUid } = await runJobNow(req.scheduleUid, reason);
      if (!jobEventUid) {
        notify({ severity: "success", title: "Job Triggered", message: `Schedule "${req.scheduleName}" has been triggered.` });
        onClose();
        return;
      }
      follow(jobEventUid);
    } catch (err) {
      setError(err instanceof Error ? err.message : "Failed to trigger schedule.");
    } finally {
      setSubmitting(false);
    }
  };

  const processRunUid = state && "processRunUid" in state ? state.processRunUid : undefined;

  return (
    <Dialog open onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>Run Now: {req.scheduleName}</DialogTitle>
      <DialogContent>
        {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
        {state || req.jobEventUid ? (
          <List dense data-testid="run-now-steps">
            {runSteps(state ?? { phase: "submitted" }).map((step, i) => (
              <ListItem key={i} disableGutters>
                <ListItemIcon sx={{ minWidth: 36 }}>{stepIcon(step.status)}</ListItemIcon>
                <ListItemText primary={step.label} slotProps={{ primary: { color: step.status === "pending" ? "text.disabled" : "text.primary" } }} />
              </ListItem>
            ))}
          </List>
        ) : (
          <TextField
            autoFocus
            fullWidth
            multiline
            minRows={2}
            margin="dense"
            label="Reason"
            value={reason}
            onChange={(e) => setReason(e.target.value)}
            disabled={submitting}
          />
        )}
      </DialogContent>
      <DialogActions>
        {(state || req.jobEventUid) && (
          <Button
            startIcon={<OpenInNewIcon />}
            disabled={!processRunUid}
            onClick={() => processRunUid && void logRunFor(processRunUid).then((uid) => window.open(ROUTES.APP.LOGS_TAIL(uid), "_blank"))}
          >
            Open logs
          </Button>
        )}
        <Button onClick={onClose}>{state || req.jobEventUid ? "Close" : "Cancel"}</Button>
        {!state && !req.jobEventUid && (
          <Button variant="contained" onClick={submit} disabled={submitting}>
            Submit
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );
};
